// Row actions for orders / payments (legacy helpers/ActionsDropdownComponent.vue
// as wired by Sale/Purchase/Quotation/ReturnOrder/Payment list components).

import { useEffect, useState, type ReactNode } from 'react';
import { Alert, CircularProgress, Stack } from '@mui/material';
import { CloseCircle, Copy, DocumentDownload, Eye, MoneyAdd, Printer, Receipt2, ShoppingCart, Sms, TickCircle, Truck, TruckFast, Wallet } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, emailRecord, openDownload } from '../../api/client';
import type { CrudActionContext, CrudRecord, CrudRowAction } from '../../crud/types';
import { t } from '../../i18n';
import { DocumentPanel, type DocumentKind } from '../documents/DocumentPanel';
import { asArray, toNumber, type AnyRecord } from '../documents/format';
import { DeliveryFormBody } from './DeliveryForm';
import { PaymentsListDialog } from './PaymentsListDialog';

// Loads a record then renders it (used by the document / delivery dialogs).
function RecordLoader({ endpoint, children }: { endpoint: string; children: (record: AnyRecord) => ReactNode }) {
  const [record, setRecord] = useState<AnyRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    adminApi
      .get(endpoint)
      .then((response) => active && setRecord(response.data as AnyRecord))
      .catch((err) => active && setError(apiErrorMessage(err, t('common.actionFailed'))));
    return () => {
      active = false;
    };
  }, [endpoint]);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!record)
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  return <>{children(record)}</>;
}

const reference = (row: CrudRecord) => String(row.reference ?? row.id);

function openDocument(ctx: CrudActionContext, resource: string, kind: DocumentKind, row: CrudRecord, receipt = false) {
  ctx.openDialog(
    () => (
      <RecordLoader endpoint={`${resource}/${row.id}`}>
        {(record) => <DocumentPanel record={record as CrudRecord} kind={kind} ctx={ctx} initialTab={receipt ? 'receipt' : 'invoice'} />}
      </RecordLoader>
    ),
    { title: `${t(`doc.kind.${kind}`)} (${reference(row)})`, maxWidth: receipt ? 'sm' : 'md' }
  );
}

export function printAction(resource: string, kind: DocumentKind, permission: string): CrudRowAction {
  return {
    key: 'print-document',
    label: kind === 'payment' ? 'orderActions.viewPayment' : 'orderActions.printInvoice',
    icon: Printer,
    permission,
    run: (row, ctx) => openDocument(ctx, resource, kind, row)
  };
}

export function receiptAction(): CrudRowAction {
  return {
    key: 'view-receipt',
    label: 'orderActions.viewReceipt',
    icon: Receipt2,
    permission: 'read-sales',
    visible: (row) => toNumber(row.pos) === 1,
    run: (row, ctx) => openDocument(ctx, 'sales', 'sale', row, true)
  };
}

export function emailAction(resource: string, permission: string): CrudRowAction {
  return {
    key: 'email',
    label: 'orderActions.email',
    icon: Sms,
    permission,
    run: async (row, ctx) => {
      const response = await emailRecord(resource, row.id);
      if (response.success) ctx.toast(t('orderActions.emailSent'), 'success');
      else ctx.toast(t('api.email_failed'), 'error');
    }
  };
}

export function downloadAction(resource: string, permission: string): CrudRowAction {
  return {
    key: 'download',
    label: 'orderActions.download',
    icon: DocumentDownload,
    permission,
    run: (row) => openDownload(resource, row.id)
  };
}

export function duplicateAction(resource: 'sales' | 'purchases'): CrudRowAction {
  return {
    key: 'duplicate',
    label: 'orderActions.duplicate',
    icon: Copy,
    permission: `create-${resource}`,
    run: (row, ctx) => ctx.openCreate({ duplicate_of: row.id, resource })
  };
}

export function toggleVoidActions(resource: 'sales' | 'purchases'): CrudRowAction[] {
  const run = async (row: CrudRecord, ctx: CrudActionContext) => {
    const response = await adminApi.delete(`${resource}/${row.id}/toggle_void`);
    const data = response.data as { success?: boolean; void?: unknown };
    if (!data?.success) {
      ctx.toast(t('common.actionFailed'), 'error');
      return;
    }
    const isVoid = data.void === true || toNumber(data.void) === 1;
    ctx.toast(t('orderActions.markedAs', { x: isVoid ? t('orderActions.void') : t('orderActions.valid') }), 'success');
    await ctx.reload();
  };
  return [
    {
      key: 'mark-void',
      label: 'orderActions.markVoid',
      icon: CloseCircle,
      color: 'error',
      permission: `delete-${resource}`,
      visible: (row) => toNumber(row.void) !== 1,
      confirm: (row) => t('orderActions.markVoidConfirm', { reference: reference(row) }),
      run
    },
    {
      key: 'mark-valid',
      label: 'orderActions.markValid',
      icon: TickCircle,
      color: 'success',
      permission: `delete-${resource}`,
      visible: (row) => toNumber(row.void) === 1,
      confirm: (row) => t('orderActions.markValidConfirm', { reference: reference(row) }),
      run
    }
  ];
}

// Navigate to the payments page with the create form pre-filled (legacy
// /payments/add?sale_id=…&customer_id=…&amount=…).
export function addPaymentAction(source: 'sale' | 'purchase' | 'return'): CrudRowAction {
  return {
    key: 'add-payment',
    label: 'orderActions.addPayment',
    icon: MoneyAdd,
    permission: 'create-payments',
    disabled: (row) => source !== 'return' && toNumber(row.paid) === 1,
    run: (row, ctx) => {
      const params = new URLSearchParams({ create: '1' });
      if (source === 'sale') {
        params.set('type', 'customer');
        params.set('sale_id', String(row.id));
        params.set('customer_id', String(row.customer_id ?? ''));
      } else if (source === 'purchase') {
        params.set('type', 'supplier');
        params.set('purchase_id', String(row.id));
        params.set('supplier_id', String(row.supplier_id ?? ''));
      } else {
        params.set('return_id', String(row.id));
        if (row.type === 'purchase' && row.supplier_id) {
          params.set('type', 'supplier');
          params.set('supplier_id', String(row.supplier_id));
        } else {
          params.set('type', 'customer');
          params.set('customer_id', String(row.customer_id ?? ''));
        }
      }
      params.set('amount', String(toNumber(row.grand_total)));
      params.set('max_amount', String(toNumber(row.grand_total)));
      ctx.navigate(`/payments?${params.toString()}`);
    }
  };
}

export function listPaymentsAction(resource: 'sales' | 'purchases'): CrudRowAction {
  return {
    key: 'list-payments',
    label: 'orderActions.listPayments',
    icon: Wallet,
    permission: 'read-payments',
    run: (row, ctx) => {
      ctx.openDialog(() => <PaymentsListDialog endpoint={`${resource}/${row.id}/payments`} />, {
        title: `${t('orderActions.listPayments')} (${reference(row)})`,
        maxWidth: 'lg'
      });
    }
  };
}

const deliveries = (row: CrudRecord) => asArray(row.deliveries);

function openDeliveryForm(ctx: CrudActionContext, options: { saleId?: string | number; deliveryId?: string | number }, title: string) {
  ctx.openDialog(
    (close) => (
      <DeliveryFormBody
        saleId={options.saleId}
        deliveryId={options.deliveryId}
        onCancel={close}
        onSaved={() => {
          close();
          ctx.toast(options.deliveryId ? t('orderActions.deliveryUpdated') : t('orderActions.deliveryAdded'), 'success');
          void ctx.reload();
        }}
      />
    ),
    { title, maxWidth: 'md' }
  );
}

export function deliveryActions(): CrudRowAction[] {
  return [
    {
      key: 'add-delivery',
      label: 'orderActions.addDelivery',
      icon: TruckFast,
      permission: 'create-deliveries',
      visible: (row) => Array.isArray(row.deliveries) && deliveries(row).length === 0,
      disabled: (row) => toNumber(row.draft) === 1 || toNumber(row.delivered) === 1,
      run: (row, ctx) => {
        if (toNumber(row.draft) === 1) {
          ctx.toast(t('orderActions.deliveryDraftBlocked'), 'warning');
          return;
        }
        openDeliveryForm(ctx, { saleId: row.id }, `${t('orderActions.addDelivery')} (${reference(row)})`);
      }
    },
    {
      key: 'edit-delivery',
      label: 'orderActions.editDelivery',
      icon: Truck,
      permission: 'update-deliveries',
      visible: (row) => deliveries(row).length > 0,
      run: (row, ctx) => openDeliveryForm(ctx, { deliveryId: String(deliveries(row)[0].id) }, `${t('orderActions.editDelivery')} (${reference(row)})`)
    },
    {
      key: 'view-delivery',
      label: 'orderActions.viewDelivery',
      icon: Eye,
      permission: 'read-deliveries',
      visible: (row) => deliveries(row).length > 0,
      run: (row, ctx) => {
        const delivery = deliveries(row)[0];
        ctx.openDialog(
          () => <RecordLoader endpoint={`deliveries/${delivery.id}`}>{(record) => <DocumentPanel record={record as CrudRecord} kind="delivery" ctx={ctx} />}</RecordLoader>,
          { title: `${t('doc.kind.delivery')} (${String(delivery.reference ?? reference(row))})`, maxWidth: 'md' }
        );
      }
    }
  ];
}

export function createSaleFromQuotationAction(): CrudRowAction {
  return {
    key: 'create-sale',
    label: 'orderActions.createSale',
    icon: ShoppingCart,
    permission: 'create-sales',
    run: (row, ctx) => ctx.navigate(`/sales?create=1&quotation_id=${encodeURIComponent(String(row.id))}`)
  };
}

export function reviewPaymentAction(): CrudRowAction {
  return {
    key: 'review-payment',
    label: 'orderActions.acceptPayment',
    icon: TickCircle,
    color: 'warning',
    permission: 'update-payments',
    visible: (row) => toNumber(row.review) === 1 && !row.reviewed_by && toNumber(row.received) !== 1,
    confirm: (row) => t('orderActions.acceptPaymentConfirm', { reference: reference(row) }),
    run: async (row, ctx) => {
      const response = await adminApi.post(`payments/review/${row.id}`, {});
      if ((response.data as { success?: boolean })?.success) {
        ctx.toast(t('orderActions.paymentReviewed'), 'success');
        await ctx.reload();
      } else {
        ctx.toast(t('common.actionFailed'), 'error');
      }
    }
  };
}
